import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ptBR } from "@mui/x-date-pickers/locales";
import dayjs, { Dayjs } from "dayjs";
import React from "react";

interface Props {
  assignmentTitle: string;
  currentDueDate: Date;
  currentAllowFeedback: boolean;
  instrumentAllowFeedback: boolean;
  instrumentDefaultFeedback: boolean;
  onClose: () => void;
  onSubmit: (fields: { dueDate: Date; allowFeedback?: boolean; name?: string }) => Promise<void>;
}

interface State {
  name: string;
  selectedDate?: Date;
  allowFeedback: boolean;
  isSubmitting: boolean;
  errorMessage?: string;
}

class UpdateApplicationModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      name: props.assignmentTitle,
      selectedDate: props.currentDueDate,
      allowFeedback: props.currentAllowFeedback,
      isSubmitting: false,
      errorMessage: undefined,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (
      prevProps.assignmentTitle !== this.props.assignmentTitle ||
      prevProps.currentDueDate !== this.props.currentDueDate ||
      prevProps.currentAllowFeedback !== this.props.currentAllowFeedback
    ) {
      this.setState({
        name: this.props.assignmentTitle,
        selectedDate: this.props.currentDueDate,
        allowFeedback: this.props.currentAllowFeedback,
        errorMessage: undefined,
      });
    }
  }

  private handleChangeDueDate(date: Dayjs | null) {
    this.setState({ selectedDate: date ? date.toDate() : undefined });
  }

  private async handleSubmit() {
    if (!this.state.selectedDate) {
      this.setState({ errorMessage: "Selecione uma nova data limite antes de salvar." });
      return;
    }
    this.setState({ isSubmitting: true, errorMessage: undefined });
    try {
      const fields: { dueDate: Date; allowFeedback?: boolean; name?: string } = {
        dueDate: this.state.selectedDate,
      };
      const trimmedName = this.state.name.trim();
      if (trimmedName && trimmedName !== this.props.assignmentTitle) {
        fields.name = trimmedName;
      }
      if (this.props.instrumentAllowFeedback) {
        fields.allowFeedback = this.state.allowFeedback;
      }
      await this.props.onSubmit(fields);
    } catch (error: any) {
      const message =
        error?.message ||
        (typeof error === "string" ? error : "Não foi possível atualizar a aplicação.");
      this.setState({ errorMessage: message });
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  private formatDate(date: Date) {
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  render() {
    const { instrumentAllowFeedback, instrumentDefaultFeedback } = this.props;
    const { name, selectedDate, allowFeedback, isSubmitting, errorMessage } = this.state;
    const differsFromDefault = allowFeedback !== instrumentDefaultFeedback;

    return (
      <Dialog open onClose={this.props.onClose} variant="modal" maxWidth="sm" fullWidth>
        <DialogTitle variant="modal">
          <EditNoteIcon />
          Editar aplicação
        </DialogTitle>
        <DialogContent variant="modal">
          <TextField
            label="Nome da aplicação"
            value={name}
            onChange={(e) => this.setState({ name: e.target.value })}
            disabled={isSubmitting}
            variant="outlined"
            fullWidth
            required
          />

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={
              ptBR.components.MuiLocalizationProvider.defaultProps.localeText
            }
          >
            <MobileDateTimePicker
              label="Novo prazo para conclusão"
              format="D/M/YYYY HH:mm"
              ampm={false}
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={(date) => this.handleChangeDueDate(date)}
              sx={{ mt: 3, width: "100%" }}
              slotProps={{
                textField: { variant: "outlined", required: true },
              }}
              disabled={isSubmitting}
            />
          </LocalizationProvider>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Prazo atual: {this.formatDate(this.props.currentDueDate)}
          </Typography>

          {instrumentAllowFeedback && (
            <>
              <Divider sx={{ mt: 2.5 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={allowFeedback}
                    onChange={(e) => this.setState({ allowFeedback: e.target.checked })}
                    disabled={isSubmitting}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

                    Permitir que o respondente acesse sua devolutiva individual ao terminar de responder
                  </Typography>
                }
                sx={{ mt: 2 }}
              />

              {differsFromDefault && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {allowFeedback
                    ? "Com essa configuração, o respondente ACESSARÁ a devolutiva ao terminar de responder."
                    : "Com essa configuração, o respondente NÃO ACESSARÁ a devolutiva ao terminar de responder."}
                </Alert>
              )}
            </>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions variant="modal" sx={{ gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="error"
            onClick={this.props.onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => this.handleSubmit()}
            disabled={!selectedDate || !name.trim() || isSubmitting}
          >
            {isSubmitting ? "Salvando..." : "Salvar alterações"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default UpdateApplicationModal;
